import React, { useState, useEffect } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Script } from 'gatsby';
import Stack from '../stack/Stack';
import Typography from '../typography/Typography';
import { Play } from '../icon';
import { useBackgroundColor } from '../../utils';
import Image from '../image/Image';
import { useDataLayerPush } from '../../hooks/useDataLayerPush';

export interface VideoProps {
  url: string;
  overlayImage: string;
  title: string;
}

interface StyledOverlayProps {
  isActive: boolean;
}

interface StyledPlayer {
  isLoaded: boolean;
}

const StyledVideo = styled.div`
  ${({ theme: { borderRadii, colors } }) => css`
    position: relative;
    cursor: pointer;
    transition: all 0.2s ease-in-out;

    iframe {
      overflow: hidden;
      position: relative;
      border-radius: ${borderRadii.small};
      border: solid 1px currentColor;

      &:hover {
        box-shadow: 0 0 0 1px ${colors.darkBlue};
      }
    }
  `};
`;

const StyledOverlay = styled.button<StyledOverlayProps>`
  ${({ theme: { space, colors, media, layers, borderRadii }, isActive }) => css`
    display: ${isActive ? 'block' : 'none'};
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: ${layers.videoOverlay};
    border-radius: ${borderRadii.small};
    border: solid 1px currentColor;
    overflow: hidden;

    &:hover {
      box-shadow: 0 0 0 1px ${colors.darkBlue};
    }

    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 4px ${colors.warningYellow};
    }
  `};
`;

const StyledPlayer = styled.div`
  ${({ theme: { space, colors, media } }) => css`
    position: relative;
    width: 100%;
    padding-top: 56.25%; /* 16:9 aspect ratio (height:width ratio) */

    iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  `};
`;

const StyledHeading = styled.div`
  ${({ theme: { colors, space, borderRadii } }) => css`
    text-align: center;
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    display: grid;
    place-items: center;
    background-color: ${colors.darkBlueOpacity};
    color: ${colors.white};
    padding: 0 ${space.small};

    svg {
      margin-left: auto;
      margin-right: auto;
    }
  `};
`;

export const Video: React.FC<VideoProps> = ({ url, overlayImage, title }) => {
  const { dataLayerPush } = useDataLayerPush();
  const [player, setPlayer] = useState<YT.Player | null>(null);
  const [showOverlay, setShowOverlay] = useState(true);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [playerLoaded, setPlayerLoaded] = useState(false);
  const [uniqueId, setUniqueId] = useState(
    `video-${Math.random().toString(36).substr(2, 9)}`
  );

  useEffect(() => {
    // Initialize YouTube Player once the script is loaded
    window.onYouTubeIframeAPIReady = () => {
      setScriptLoaded(true);
    };

    return () => {
      if (window.onYouTubeIframeAPIReady) {
        delete window.onYouTubeIframeAPIReady;
      }
    };
  }, []);

  useEffect(() => {
    if (scriptLoaded && player === null) {
      initializePlayer();
    }
  }, [scriptLoaded, player]);

  const initializePlayer = () => {
    const newPlayer = new window.YT.Player(uniqueId, {
      videoId: getVideoId(url),
      events: {
        onReady: onPlayerReady,
        onStateChange: onPlayerStateChange,
      },
      playerVars: {
        rel: 0,
        autoplay: 0,
      },
    });
    setPlayer(newPlayer);
  };

  const onPlayerReady = () => {
    setPlayerLoaded(true);
  };

  const onPlayerStateChange = (event: any) => {
    if (event.data === window.YT.PlayerState.PAUSED) {
      setShowOverlay(true); // Show overlay when the video is paused
    }
  };

  const handleOverlayClick = () => {
    setShowOverlay(false);
    dataLayerPush({
      event: 'video_play',
      video_title: title,
    });
    if (player) {
      if (typeof player.playVideo === 'function') {
        player.playVideo(); // Resume video playback
      }
    }
  };

  const getVideoId = (videoUrl: string) => {
    if (videoUrl?.includes('youtube.com')) {
      const match = videoUrl.match(/(?:\/|v=)([a-zA-Z0-9_-]{11})/);
      return match ? match[1] : '';
    }
    return videoUrl;
  };

  return (
    <>
      <Script id="first-unique-id" src="https://www.youtube.com/iframe_api" />
      <StyledVideo>
        <StyledOverlay isActive={showOverlay} onClick={handleOverlayClick}>
          <Image src={overlayImage} />
          <StyledHeading>
            <Stack gap="xxxSmall">
              <Play size="xLarge" />
              <Typography component="h3" variant="secondaryHeadingSmallBold">
                {title}
              </Typography>
            </Stack>
          </StyledHeading>
        </StyledOverlay>
        {url && (
          <StyledPlayer>
            <div id={uniqueId} style={{ width: '100%', height: '100%' }} />
          </StyledPlayer>
        )}
      </StyledVideo>
    </>
  );
};
